import React, { useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import errorBlue from '../../../../../assets/icons/errorBlue.svg';
import maintenance from '../../../../../assets/icons/maintenance.svg';
import infoIcon from '../../../../../assets/icons/i.svg';
import devicesIcon from '../../../../../assets/icons/Devices.svg';
import checkGreen from '../../../../../assets/icons/checkGreen.svg';

import { title } from 'process';
import { IconButton } from '@mui/material';
import { useRouteContext } from 'packages/novous_web/src/context/RouteContext';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { formatTime } from 'packages/novous_web/src/utils/helper';
import PermissionGuard from '../../../../../common/components/PermissionGuard';
import { PermissionSubcategory } from '../../../../../utils/permissionConstants';

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

const DeviceDataTiles: React.FC<{
  data: ColumnObjType | undefined;
  selectedGroup?: string | null;
  allDeviceData?: any;
  selectedUser?: any;
  initialDeviceTilesCount?: {
    totalDevice?: number;
    faultyDevices?: number;
  };
}> = ({ data, selectedGroup, allDeviceData, selectedUser, initialDeviceTilesCount }) => {
  const { user } = useAuth();
  const [countData, setCountData] = useState<any>();
  const [faultyDeviceCount, setFaultyDeviceCount] = useState<number>(0);
  const [useInitialTilesCount, setUseInitialTilesCount] = useState<boolean>(
    Boolean(initialDeviceTilesCount),
  );
  const [hasShownFaultyTile, setHasShownFaultyTile] = useState(false);

  let tripTilesData = [
    {
      icon: devicesIcon,
      heading: 'Total Devices',
      value: 0,
      tooltip: 'Total number of devices registered in the fleet',
      permission: {
        category: PermissionSubcategory.DEVICES,
        subcategory: PermissionSubcategory.TOTAL_DEVICES,
      },
    },
    {
      icon: errorBlue,
      heading: 'Faulty Devices',
      value: 0,
      tooltip: 'Total number of devices with errors',
      permission: {
        category: PermissionSubcategory.DEVICES,
        subcategory: PermissionSubcategory.TOTAL_DEVICES,
      },
    },
  ];

  // const {
  //   data: deviceTilesCount,
  //   error: deviceTilesError,
  //   loading: deviceTilesLoading,
  //   fetchData: fetchDeviceTilesCount,
  // } = useAxios<any>(`${API_ENDPOINTS.directories.DEVICE_TILES_COUNT}`, 'POST');

  // useEffect(() => {
  //   fetchDeviceTilesCount({
  //     fleetId: user.customer_id || selectedUser?.customer_id,
  //     vehicleGroup: selectedGroup && selectedGroup,
  //   });
  // }, [user, selectedUser]);

  // useEffect(() => {
  //   setCountData(deviceTilesCount);
  // }, [deviceTilesCount]);

  // Calculate faulty devices count from allDeviceData
  useEffect(() => {
    if (allDeviceData?.data && Array.isArray(allDeviceData.data)) {
      const faultyCount = allDeviceData.data.filter((device: any) => device.errorStatus === 1).length;
      setFaultyDeviceCount(faultyCount);
    }
  }, [allDeviceData]);

  // Use dashboard-provided counts only for the initial open on Faulty tab.
  // Once user switches away from Faulty tab, revert permanently to existing flow.
  useEffect(() => {
    if (!initialDeviceTilesCount || !useInitialTilesCount || !data) return;

    const isFaultyTileVisible = Object.values(data).some(
      (stat) => stat.name === 'Faulty Devices' && stat.status,
    );

    if (isFaultyTileVisible) {
      setHasShownFaultyTile(true);
      return;
    }

    if (hasShownFaultyTile && !isFaultyTileVisible) {
      setUseInitialTilesCount(false);
    }
  }, [data, hasShownFaultyTile, initialDeviceTilesCount, useInitialTilesCount]);

  tripTilesData[0].value = Array.isArray(allDeviceData?.data) ? allDeviceData.data.length : 0;
  tripTilesData[1].value = useInitialTilesCount
    ? initialDeviceTilesCount?.faultyDevices ?? faultyDeviceCount
    : faultyDeviceCount;

  const filteredTileData = useMemo(() => {
    return tripTilesData.filter((item) => {
      if (data) {
        // Find if any stat has this item's heading and is active
        const matchingStat = Object.values(data).find(
          (stat) => stat.name === item.heading && stat.status === true,
        );
        return !!matchingStat;
      } else {
        return true;
      }
    });
  }, [data, tripTilesData]);

  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
      {filteredTileData.map((tile, index) => (
        <PermissionGuard
          key={index}
          category={tile.permission.category}
          subcategory={tile.permission.subcategory}
        >
          <Paper
            sx={{ borderRadius: '12px' }}
            className="border border-[#f0eff8] p-4 flex flex-col gap-1 items-start"
          >
            <div className="flex items-center gap-2 w-full">
              <IconButton
                sx={(theme) => ({
                  background: theme.palette.selected,
                  padding: '0px',
                  borderRadius: '8px',
                })}
              >
                <img src={tile?.icon} alt="icon" color="primary" />
              </IconButton>
              <div className="flex flex-1 min-w-0 flex-col justify-start gap-1">
                <Typography
                  variant="body2"
                  noWrap // Ensures text doesn't wrap and remains in a single line
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%', // Ensures it adapts to available space
                  }}
                >
                  {tile.heading}
                </Typography>
              </div>

              <Tooltip title={tile.tooltip} arrow placement="bottom">
                <img src={infoIcon} alt="info icon" className="w-4 h-4 cursor-pointer" />
              </Tooltip>
            </div>
            <div className="flex items-center w-full gap-2 flex-nowrap">
              <div className="invisible w-6 h-6">{tile.icon}</div>
              <Typography
                variant="h6"
                fontWeight={600}
                className="flex-grow px-2 w-full whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {tile.value}
              </Typography>
            </div>
          </Paper>
        </PermissionGuard>
      ))}
    </div>
  );
};

export default DeviceDataTiles;
